import { h, FunctionComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  PPCheckmark,
  PPCheckmarkUnselected,
} from '~src/domains/common/assets/svg';
import CurrencyText from '~src/domains/common/components/CurrencyText';
import { Promo } from '~src/domains/common/enums/api';
import { Brand, Keys } from '~src/domains/common/enums/common';
import { setLoanOfferSelection } from '~src/domains/common/state/commonReducer';
import {
  brandSelector,
  loanOfferSelectionSelector,
  loanOffersSelector,
  localeSelector,
  upcodeSelector,
} from '~src/domains/common/state/commonSelectors';
import * as styles from '~src/styles/domains/accept/components/OfferSelector.module.scss';
import { trackClick } from '~src/utils/tracking';
import { OfferSelectorProps } from '../types/components';
import OffersTag from './OffersTag';
import { OfferLabelType } from '~src/domains/common/types/offers';
import { OFFERS_TAG_ENABLED } from '~src/utils/constants';

const OfferSelector: FunctionComponent<OfferSelectorProps> = ({
  isMultipleOfferFlow,
  index,
  offer,
  showFrom = false,
  hideAPR = false,
  isLowest = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const loanOfferSelection = useSelector(loanOfferSelectionSelector);
  const loanOffers = useSelector(loanOffersSelector);
  const locale = useSelector(localeSelector);
  const brand = useSelector(brandSelector);
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const isUplift = brand == Brand.UPLIFT;
  const upc = useSelector(upcodeSelector);

  const {
    pay_today,
    apr,
    monthly_payment_amount,
    total_interest = 0,
    grand_total,
    number_of_payments,
    offer_id,
    promos,
  } = offer;
  const isZeroDown = pay_today === 0;
  const isSubvented = apr === 0;
  const delayedPaymentOffer = promos?.find(promo =>
    [
      Promo.ONE_MONTH_DELAYED,
      Promo.THREE_MONTH_DELAYED,
      Promo.SIX_MONTH_DELAYED,
      Promo.SEVEN_MONTH_DELAYED,
    ].includes(promo),
  );

  useEffect(() => {
    setIsSelected(loanOfferSelection ? loanOfferSelection === offer_id : false);
  }, [loanOfferSelection]);

  const setLoanOffer = () => {
    const loanOfferCopy = Object.assign(
      {},
      loanOffers.find(offer => offer.offer_id === offer_id),
    );

    delete loanOfferCopy.docs;
    delete loanOfferCopy.localization;
    delete loanOfferCopy['offer?'];

    batch(() => {
      dispatch(setLoanOfferSelection(offer_id));
      dispatch(
        trackClick('payment-plan', {
          'num-payments': number_of_payments,
          option: loanOfferCopy,
        }),
      );
    });
  };

  const clickHandler = (e: any) => {
    const target = e.target;
    const tooltipElement = document.querySelector(
      `#pay-today-tooltip-${offer.offer_id}`,
    );
    const tooltipParent = tooltipElement && tooltipElement.parentElement;

    if (
      !showFrom &&
      ((isMultipleOfferFlow && !tooltipElement) ||
        (tooltipParent &&
          target !== tooltipParent &&
          !tooltipParent.contains(target))) &&
      offer_id !== loanOfferSelection
    ) {
      setLoanOffer();
    }
  };

  const onKeyDown = (e: any) => {
    const key = e.key;

    if (
      !showFrom &&
      isMultipleOfferFlow &&
      (key === Keys.ENTER || key === Keys.SPACEBAR || key === Keys.SPACE) &&
      offer_id !== loanOfferSelection
    ) {
      setLoanOffer();
    }
  };

  const getMonthsDelayed = (promo: Promo) => {
    switch (promo) {
      case Promo.ONE_MONTH_DELAYED:
        return 1;
      case Promo.THREE_MONTH_DELAYED:
        return 3;
      case Promo.SIX_MONTH_DELAYED:
        return 6;
      case Promo.SEVEN_MONTH_DELAYED:
        return 7;
      default:
        return 0;
    }
  };

  return (
    <div
      key={`loan-offer-${offer_id}`}
      className={
        (isMultipleOfferFlow && isSelected) || !isMultipleOfferFlow
          ? `${styles['new-plan-info-wrapper']} ${styles['new-plan-selected']}`
          : styles['new-plan-info-wrapper']
      }
      role={isMultipleOfferFlow ? 'button' : ''}
      aria-pressed={isMultipleOfferFlow ? isSelected : 'true'}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onClick={clickHandler}
    >
      {upc &&
        OFFERS_TAG_ENABLED.includes(upc) &&
        (offer.apr === 0 || isLowest) && (
          <OffersTag
            type={
              offer.apr === 0
                ? OfferLabelType.ZERO_APR
                : OfferLabelType.LOWEST_MONTHLY_PAYMENT
            }
          />
        )}
      <span className='sr-only'>
        <Trans
          t={t}
          i18nKey={
            isMultipleOfferFlow && isSelected
              ? 'accept:offerSelector.srOnly.multipleOfferSelectedTitle'
              : isMultipleOfferFlow
              ? 'accept:offerSelector.srOnly.multipleOfferTitle'
              : 'accept:offerSelector.srOnly.singleOfferTitle'
          }
          values={{
            index: isMultipleOfferFlow ? index + 1 : undefined,
          }}
        />
      </span>
      {delayedPaymentOffer && (
        <div className={styles['new-plan-promo']}>
          <span>
            <Trans
              t={t}
              i18nKey={
                delayedPaymentOffer == Promo.ONE_MONTH_DELAYED
                  ? 'accept:flexpay.offerSelector.delayedPayments.single'
                  : 'accept:flexpay.offerSelector.delayedPayments.multi'
              }
              values={{ months: getMonthsDelayed(delayedPaymentOffer) }}
            />
          </span>
        </div>
      )}
      <div
        className={styles['new-plan-info']}
        aria-live='polite'
        aria-atomic={true}
      >
        <div className={styles['new-plan-info-monthly-payment']}>
          {isSelected ? (
            <PPCheckmark color={isUplift ? '#1fab9e' : '#1f63d2'} />
          ) : (
            <PPCheckmarkUnselected />
          )}
          <div className={styles['new-plan-info-monthly-payment-amount']}>
            <div className={styles['new-plan-info-monthly-payment-value']}>
              <CurrencyText value={monthly_payment_amount} locale={locale} />
            </div>
            <div className={styles['new-plan-info-monthly']}>
              {t('accept:offerSelector.monthly')}
            </div>
          </div>
        </div>
        <div className={styles['new-plan-info-remaining-payments']}>
          <span className='sr-only'>
            {t('accept:offerSelector.srOnly.remainingPayments')}
          </span>
          {`${number_of_payments} `}
          {t('accept:offerSelector.months')}
        </div>
      </div>
      <div className={styles['new-plan-downpayment']}>
        {isZeroDown ? (
          <span>{t('accept:offerSelector.zeroDown')}</span>
        ) : (
          <Trans
            t={t}
            i18nKey='accept:offerSelector.payToday'
            components={{
              downPayment: <CurrencyText value={pay_today} locale={locale} />,
            }}
          />
        )}
      </div>
      <div className={styles['new-plan-details']}>
        {!hideAPR && (
          <div className={styles['new-plan-details-column']}>
            <span>{t('accept:offerSelector.apr')}</span>
            <span
              className={
                isSubvented
                  ? `${styles['new-plan-subvented']} ${styles['new-plan-details-value']}`
                  : styles['new-plan-details-value']
              }
            >
              <CurrencyText
                value={apr}
                locale={locale}
                isApr={true}
                round={false}
              />
            </span>
          </div>
        )}

        <div className={styles['new-plan-details-column']}>
          <span>{t('accept:offerSelector.interest')}</span>
          <span className={styles['new-plan-details-value']}>
            <CurrencyText value={total_interest} locale={locale} />
          </span>
        </div>
        <div className={styles['new-plan-details-column']}>
          <span>{t('accept:offerSelector.total')}</span>
          <span className={styles['new-plan-details-value']}>
            <CurrencyText value={grand_total} locale={locale} />
          </span>
        </div>
      </div>
    </div>
  );
};

export default OfferSelector;
